import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, GitCommitHorizontal, GitCompare, History, Lock, MessageSquare, RotateCcw, User, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useUserLookup } from '../../../../hooks/useUserLookup';
import { projectDataClient } from '../../../../lib/connect/clients';
import { useConfirm } from '../shared/ConfirmDialog';
import { isConcurrentEdit, isLocked, provenanceLabel } from './sectionRevision';
/**
 * VersionHistoryDrawer — right-side panel for a section's document history.
 * Working revisions and locked milestone / review snapshots are listed apart;
 * any two versions can be compared side by side, a review snapshot can be
 * created, and a restore adds a new revision (the restored one never changes).
 */ export function VersionHistoryDrawer({ isOpen, onClose, projectId, entityType, entityId, entityName, onRestore, beforeChange }) {
    const userLookup = useUserLookup(projectId);
    const queryClient = useQueryClient();
    const confirmDialog = useConfirm();
    const [restoringVersion, setRestoringVersion] = useState(null);
    const [notice, setNotice] = useState('');
    const [problem, setProblem] = useState('');
    const [compareFrom, setCompareFrom] = useState(null);
    const [compare, setCompare] = useState(null);
    const [snapshotOpen, setSnapshotOpen] = useState(false);
    const [snapshotLabel, setSnapshotLabel] = useState('');
    const [snapshotComment, setSnapshotComment] = useState('');
    const [snapshotting, setSnapshotting] = useState(false);
    const queryKey = [
        'versionHistory',
        entityType,
        entityId
    ];
    const { data: versions, isLoading, isError, refetch } = useQuery({
        queryKey,
        queryFn: async ()=>{
            if (entityType !== 'section') return [];
            const resp = await projectDataClient.listSectionVersions({
                projectId,
                sectionId: entityId,
                limit: 100
            });
            return (resp.versions ?? []).map((v)=>({
                    id: v.id,
                    version: v.version,
                    kind: v.kind || 'working',
                    label: v.label,
                    createdBy: v.createdBy,
                    createdAt: v.createdAt?.toDate(),
                    message: v.message,
                    preview: v.contentText || '',
                    provenance: v.provenance,
                    provenanceFileName: v.provenanceFileName,
                    parentVersionId: v.parentVersionId,
                    sourceVersionId: v.sourceVersionId
                }));
        },
        enabled: isOpen && !!entityId
    });
    // Every opening shows the section as it is now, not as it was last time.
    useEffect(()=>{
        if (!isOpen) return;
        setNotice('');
        setProblem('');
        setCompare(null);
        setCompareFrom(null);
        if (entityId) refetch();
    }, [
        isOpen,
        entityId,
        refetch
    ]);
    const working = (versions ?? []).filter((v)=>!isLocked(v.kind));
    const locked = (versions ?? []).filter((v)=>isLocked(v.kind));
    const headId = versions?.[0]?.id;
    const authorOf = (v)=>v.createdBy ? userLookup.get(v.createdBy) || v.createdBy.slice(0, 8) : 'System';
    const pickForCompare = async (v)=>{
        setProblem('');
        if (!compareFrom) {
            setCompareFrom(v);
            setCompare(null);
            return;
        }
        const [older, newer] = compareFrom.version <= v.version ? [
            compareFrom,
            v
        ] : [
            v,
            compareFrom
        ];
        try {
            const resp = await projectDataClient.compareSectionVersions({
                projectId,
                sectionId: entityId,
                fromVersion: older.version,
                toVersion: newer.version
            });
            setCompare({
                from: older,
                to: newer,
                fromText: resp.from?.contentText ?? '',
                toText: resp.to?.contentText ?? '',
                lines: resp.lines.map((l)=>({
                        op: l.op,
                        text: l.text
                    }))
            });
            setCompareFrom(null);
        } catch (err) {
            console.error('Failed to compare versions:', err);
            setProblem(`Couldn't compare v${older.version} with v${newer.version}. Try again.`);
        }
    };
    const handleRestore = async (v)=>{
        if (entityType !== 'section') return;
        setRestoringVersion(v.version);
        setProblem('');
        try {
            await beforeChange?.();
            const resp = await projectDataClient.restoreVersion({
                projectId,
                sectionId: entityId,
                version: v.version,
                baseVersionId: headId ?? ''
            });
            setNotice(`Restored v${v.version} as new revision v${resp.version}. v${v.version} is unchanged and stays in the history.`);
            setCompare(null);
            await queryClient.invalidateQueries({
                queryKey
            });
            onRestore?.();
        } catch (err) {
            console.error('Failed to restore version:', err);
            setProblem(isConcurrentEdit(err) ? 'Someone saved this section while you were looking. Reopen the history to see their change, then restore again.' : `Couldn't restore v${v.version}. Nothing was changed.`);
            if (isConcurrentEdit(err)) await queryClient.invalidateQueries({
                queryKey
            });
        } finally{
            setRestoringVersion(null);
        }
    };
    const handleSnapshot = async ()=>{
        setSnapshotting(true);
        setProblem('');
        try {
            await beforeChange?.();
            const resp = await projectDataClient.createReviewSnapshot({
                projectId,
                sectionId: entityId,
                label: snapshotLabel.trim(),
                comment: snapshotComment.trim()
            });
            setNotice(`Review snapshot v${resp.version} created. It is locked and can't be edited.`);
            setSnapshotOpen(false);
            setSnapshotLabel('');
            setSnapshotComment('');
            await queryClient.invalidateQueries({
                queryKey
            });
        } catch (err) {
            console.error('Failed to create review snapshot:', err);
            setProblem(ConnectError.from(err).code === Code.FailedPrecondition ? 'This section has no text yet. Write something before creating a review snapshot.' : "Couldn't create the review snapshot. Nothing was changed.");
        } finally{
            setSnapshotting(false);
        }
    };
    if (!isOpen) return null;
    const renderRow = (v)=>{
        const prov = provenanceLabel(v);
        const picked = compareFrom?.version === v.version;
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.proposalVersions.versionItem,
            "data-version": v.version,
            "data-kind": v.kind,
            className: `rounded-lg border p-3 ${isLocked(v.kind) ? 'border-violet bg-violet-muted' : 'border-border bg-muted'}`,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center justify-between",
                    children: [
                        /*#__PURE__*/ _jsxs("span", {
                            className: "text-[length:var(--fs-ui-meta)] font-semibold text-foreground",
                            children: [
                                "v",
                                v.version,
                                v.id === headId && /*#__PURE__*/ _jsx("span", {
                                    className: "ml-1.5 rounded-full bg-success-muted px-1.5 py-px text-[length:var(--fs-ui-meta)] font-bold text-success",
                                    children: "LATEST"
                                })
                            ]
                        }),
                        isLocked(v.kind) && /*#__PURE__*/ _jsxs("span", {
                            className: "inline-flex items-center gap-1 rounded border border-violet bg-card px-1.5 py-px text-[length:var(--fs-ui-meta)] font-medium text-violet",
                            children: [
                                /*#__PURE__*/ _jsx(Lock, {
                                    className: "h-3 w-3"
                                }),
                                v.kind === 'review' ? 'Review snapshot' : 'Milestone'
                            ]
                        })
                    ]
                }),
                v.label && /*#__PURE__*/ _jsx("div", {
                    className: "mt-1 text-[length:var(--fs-ui-meta)] font-medium text-foreground",
                    children: v.label
                }),
                v.message && /*#__PURE__*/ _jsxs("div", {
                    className: "mt-1.5 flex items-start gap-1.5 text-[length:var(--fs-ui-meta)] leading-relaxed text-foreground",
                    children: [
                        /*#__PURE__*/ _jsx(MessageSquare, {
                            className: "mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            children: v.message
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "mt-1.5 flex items-center gap-2 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                    children: [
                        /*#__PURE__*/ _jsxs("span", {
                            className: "flex items-center gap-1",
                            children: [
                                /*#__PURE__*/ _jsx(User, {
                                    className: "h-3 w-3"
                                }),
                                authorOf(v)
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "flex items-center gap-1",
                            children: [
                                /*#__PURE__*/ _jsx(Clock, {
                                    className: "h-3 w-3"
                                }),
                                v.createdAt ? formatRelative(v.createdAt) : ''
                            ]
                        })
                    ]
                }),
                prov && /*#__PURE__*/ _jsx("div", {
                    className: "mt-1 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                    children: prov
                }),
                v.preview && /*#__PURE__*/ _jsx("div", {
                    className: "mt-2 rounded border border-border bg-card px-2 py-1.5 text-[length:var(--fs-ui-meta)] leading-relaxed text-foreground",
                    children: v.preview.length > 150 ? `${v.preview.slice(0, 147)}...` : v.preview
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "mt-2 flex items-center gap-3",
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraButton, {
                            variant: "link",
                            size: "inline",
                            type: "button",
                            "data-testid": Testids.proposalVersions.compareStart,
                            onClick: ()=>pickForCompare(v),
                            className: "inline-flex items-center gap-1 text-[length:var(--fs-ui-meta)] font-medium text-brand hover:text-brand",
                            children: [
                                /*#__PURE__*/ _jsx(GitCompare, {
                                    className: "h-3 w-3"
                                }),
                                compareFrom && !picked ? `Compare with v${compareFrom.version}` : picked ? 'Picked — choose another' : 'Compare'
                            ]
                        }),
                        v.id !== headId && /*#__PURE__*/ _jsxs(SpecraButton, {
                            variant: "link",
                            size: "inline",
                            type: "button",
                            "data-testid": Testids.proposalVersions.restoreButton,
                            onClick: async ()=>{
                                const ok = await confirmDialog({
                                    title: `Restore v${v.version}?`,
                                    description: `This adds a new revision on top with v${v.version}'s text. v${v.version} and every other version stay exactly as they are.`,
                                    variant: 'info',
                                    confirmLabel: 'Restore as new revision'
                                });
                                if (ok) handleRestore(v);
                            },
                            disabled: restoringVersion === v.version,
                            className: "inline-flex items-center gap-1 text-[length:var(--fs-ui-meta)] font-medium text-warning hover:text-warning disabled:opacity-50",
                            children: [
                                /*#__PURE__*/ _jsx(RotateCcw, {
                                    className: "h-3 w-3"
                                }),
                                restoringVersion === v.version ? 'Restoring...' : 'Restore'
                            ]
                        })
                    ]
                })
            ]
        }, v.id);
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]",
                onClick: onClose,
                role: "presentation"
            }),
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.proposalVersions.drawer,
                className: "fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-border bg-card shadow-xl sm:w-[34rem]",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3 border-b border-border px-4 py-3",
                        children: [
                            /*#__PURE__*/ _jsx(History, {
                                className: "h-4 w-4 text-muted-foreground"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm font-semibold text-foreground",
                                        children: "Version history"
                                    }),
                                    entityName && /*#__PURE__*/ _jsx("div", {
                                        className: "truncate text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                        children: entityName
                                    })
                                ]
                            }),
                            entityType === 'section' && /*#__PURE__*/ _jsxs(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                type: "button",
                                "data-testid": Testids.proposalVersions.snapshotOpen,
                                onClick: ()=>setSnapshotOpen((o)=>!o),
                                className: "inline-flex items-center gap-1 rounded-md border border-violet bg-violet-muted px-2 py-1 text-[length:var(--fs-ui-meta)] font-medium text-violet hover:bg-violet-muted",
                                children: [
                                    /*#__PURE__*/ _jsx(Lock, {
                                        className: "h-3 w-3"
                                    }),
                                    "Create review snapshot"
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                type: "button",
                                onClick: onClose,
                                "aria-label": "Close version history",
                                className: "rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground",
                                children: /*#__PURE__*/ _jsx(X, {
                                    className: "h-4 w-4"
                                })
                            })
                        ]
                    }),
                    snapshotOpen && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2 border-b border-border bg-violet-muted px-4 py-3",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-[length:var(--fs-ui-meta)] text-foreground",
                                children: "Locks the section's current text as a formal review record. It can't be edited or deleted afterwards."
                            }),
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                "data-testid": Testids.proposalVersions.snapshotLabel,
                                value: snapshotLabel,
                                onChange: (e)=>setSnapshotLabel(e.target.value),
                                placeholder: "Label, e.g. Pink team",
                                "aria-label": "Snapshot label",
                                className: "w-full rounded border border-border px-2 py-1 text-xs"
                            }),
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                "data-testid": Testids.proposalVersions.snapshotComment,
                                value: snapshotComment,
                                onChange: (e)=>setSnapshotComment(e.target.value),
                                placeholder: "Comment for reviewers (optional)",
                                "aria-label": "Snapshot comment",
                                rows: 2,
                                className: "w-full rounded border border-border px-2 py-1 text-xs"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "link",
                                size: "inline",
                                type: "button",
                                "data-testid": Testids.proposalVersions.snapshotSubmit,
                                onClick: handleSnapshot,
                                disabled: snapshotting,
                                className: "rounded-md bg-violet-muted px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-violet-muted disabled:opacity-50",
                                children: snapshotting ? 'Creating…' : 'Lock snapshot'
                            })
                        ]
                    }),
                    notice && /*#__PURE__*/ _jsx("output", {
                        "data-testid": Testids.proposalVersions.restoreNotice,
                        className: "block border-b border-emerald-200 bg-success-muted px-4 py-2 text-xs text-success",
                        children: notice
                    }),
                    problem && /*#__PURE__*/ _jsx("div", {
                        role: "alert",
                        className: "border-b border-danger bg-danger-muted px-4 py-2 text-xs text-danger",
                        children: problem
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex-1 overflow-y-auto",
                        children: [
                            compare && /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.proposalVersions.compareView,
                                className: "border-b border-border p-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mb-2 flex items-center justify-between text-[length:var(--fs-ui-meta)] font-semibold text-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    "Comparing v",
                                                    compare.from.version,
                                                    " → v",
                                                    compare.to.version
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                variant: "link",
                                                size: "inline",
                                                type: "button",
                                                onClick: ()=>setCompare(null),
                                                className: "font-medium text-muted-foreground hover:text-foreground",
                                                children: "Close comparison"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "grid grid-cols-2 gap-2 text-[length:var(--fs-ui-meta)]",
                                        children: [
                                            /*#__PURE__*/ _jsxs("div", {
                                                "data-testid": Testids.proposalVersions.compareFrom,
                                                className: "rounded border border-border p-2",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "mb-1 font-semibold text-muted-foreground",
                                                        children: [
                                                            "v",
                                                            compare.from.version
                                                        ]
                                                    }),
                                                    compare.lines.filter((l)=>l.op !== 'add').map((l, i)=>/*#__PURE__*/ _jsx("div", {
                                                            className: l.op === 'remove' ? 'rounded bg-danger-muted text-danger' : 'text-foreground',
                                                            children: l.text || ' '
                                                        }, `f-${i}`))
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                "data-testid": Testids.proposalVersions.compareTo,
                                                className: "rounded border border-border p-2",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "mb-1 font-semibold text-muted-foreground",
                                                        children: [
                                                            "v",
                                                            compare.to.version
                                                        ]
                                                    }),
                                                    compare.lines.filter((l)=>l.op !== 'remove').map((l, i)=>/*#__PURE__*/ _jsx("div", {
                                                            className: l.op === 'add' ? 'rounded bg-success-muted text-success' : 'text-foreground',
                                                            children: l.text || ' '
                                                        }, `t-${i}`))
                                                ]
                                            })
                                        ]
                                    }),
                                    compare.lines.every((l)=>l.op === 'equal') && /*#__PURE__*/ _jsx("div", {
                                        className: "mt-2 text-[length:var(--fs-ui-meta)] italic text-muted-foreground",
                                        children: "These two versions have the same text."
                                    })
                                ]
                            }),
                            isLoading ? /*#__PURE__*/ _jsx("div", {
                                className: "flex items-center justify-center py-16",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "h-5 w-5 animate-spin rounded-full border-2 border-border border-t-brand"
                                })
                            }) : isError ? /*#__PURE__*/ _jsxs("div", {
                                role: "alert",
                                className: "flex flex-col items-center py-16 text-center",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm text-foreground",
                                        children: "Couldn't load this section's history."
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "link",
                                        size: "inline",
                                        type: "button",
                                        onClick: ()=>refetch(),
                                        className: "mt-2 text-xs font-medium text-brand hover:text-brand",
                                        children: "Try again"
                                    })
                                ]
                            }) : !versions || versions.length === 0 ? /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col items-center justify-center py-16 text-center",
                                children: [
                                    /*#__PURE__*/ _jsx(GitCommitHorizontal, {
                                        className: "h-6 w-6 text-muted-foreground"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-2 text-sm text-muted-foreground",
                                        children: "No revisions yet"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "mt-1 max-w-[260px] text-xs text-muted-foreground",
                                        children: [
                                            "Start writing and the section's revisions appear here. Use",
                                            ' ',
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-semibold text-success",
                                                children: "Commit"
                                            }),
                                            " to name one, or create a review snapshot to lock it."
                                        ]
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-4 p-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("section", {
                                        "data-testid": Testids.proposalVersions.lockedList,
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "mb-1.5 text-[length:var(--fs-ui-meta)] font-semibold uppercase tracking-wide text-violet",
                                                children: "Locked snapshots"
                                            }),
                                            locked.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                                children: "No locked snapshots. Create a review snapshot to keep a formal record of the text."
                                            }) : /*#__PURE__*/ _jsx("div", {
                                                className: "space-y-2",
                                                children: locked.map(renderRow)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("section", {
                                        "data-testid": Testids.proposalVersions.workingList,
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "mb-1.5 text-[length:var(--fs-ui-meta)] font-semibold uppercase tracking-wide text-foreground",
                                                children: "Working revisions"
                                            }),
                                            working.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                                className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                                                children: "No working revisions."
                                            }) : /*#__PURE__*/ _jsx("div", {
                                                className: "space-y-2",
                                                children: working.map(renderRow)
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
function formatRelative(date) {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}
